import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking, PASSENGER_CATEGORIES } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import { formatDate } from '../data/dateLocale';
import StepIndicator from '../components/StepIndicator';
import BusSeatMap, { STANDARD_RELAX, LOUNGE_RELAX } from '../components/BusSeatMap';
import { ChevronDown, ChevronUp, Info, UserCircle } from 'lucide-react';

const CATEGORY_KEYS = {
  adult: { label: 'cat.adult', age: 'cat.adultAge' },
  senior: { label: 'cat.senior', age: 'cat.seniorAge' },
  youth: { label: 'cat.youth', age: 'cat.youthAge' },
  child: { label: 'cat.child', age: 'cat.childAge' },
  preschool: { label: 'cat.preschool', age: 'cat.preschoolAge' },
};

const SeatSelectionPage = () => {
  const navigate = useNavigate();
  const {
    selectedTrip,
    selectedReturnTrip,
    searchParams,
    setCurrentStep,
    passengersList,
    setPassengersList,
    seatAssignments,
    setSeatAssignments,
    buildPassengersList,
    getPassengerPrice,
  } = useBooking();
  const { t, lang } = useLanguage();

  const [activePassengerId, setActivePassengerId] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => { setCurrentStep(2); }, [setCurrentStep]);

  // Generate occupied seats deterministically from trip id (synced with TripCard)
  const occupiedSeats = useMemo(() => {
    const occupied = new Set();
    if (!selectedTrip) return occupied;
    const isLounge = selectedTrip.type === 'LUX Lounge' || selectedTrip.busType === 'lounge';
    const totalStandard = isLounge ? 22 : 32;
    const totalRelax = 10;

    // Relax seats (3-12) and Standard seats (rest)
    const relaxSeats = [3,4,5,6,7,8,9,10,11,12];
    const standardSeats = isLounge
      ? [13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34]
      : [13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,36,37,38,39,40,41,42,43,44,45];

    // Same seed as TripCard
    let h = 0;
    const s = selectedTrip.id || '';
    for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
    const seed = Math.abs(h);

    // Calculate same free seats as TripCard
    const freeStd = Math.max(1, totalStandard - (seed % Math.floor(totalStandard * 0.85)));
    const freeRlx = Math.max(1, totalRelax - ((seed >> 8) % Math.floor(totalRelax * 0.8)));
    const occupiedStd = totalStandard - freeStd;
    const occupiedRlx = totalRelax - freeRlx;

    // Deterministic PRNG
    let rng = seed;
    const next = () => { rng = (rng * 1664525 + 1013904223) & 0xffffffff; return (rng >>> 0) / 0xffffffff; };

    // Occupy Standard seats
    const shuffledStd = [...standardSeats].map(s => ({ s, r: next() })).sort((a, b) => a.r - b.r);
    for (let i = 0; i < Math.min(occupiedStd, shuffledStd.length); i++) {
      occupied.add(shuffledStd[i].s);
    }
    // Occupy Relax seats
    const shuffledRlx = [...relaxSeats].map(s => ({ s, r: next() })).sort((a, b) => a.r - b.r);
    for (let i = 0; i < Math.min(occupiedRlx, shuffledRlx.length); i++) {
      occupied.add(shuffledRlx[i].s);
    }
    return occupied;
  }, [selectedTrip]);

  // Initialize passengers list and auto-assign random seats
  useEffect(() => {
    if (passengersList.length === 0) {
      const list = buildPassengersList();
      setPassengersList(list);
      if (list.length > 0) setActivePassengerId(list[0].id);

      // Don't auto-assign — let user pick seats themselves
      setSeatAssignments({});
    } else {
      if (!activePassengerId) setActivePassengerId(passengersList[0]?.id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (openDropdown !== null) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(e.target)) {
          setOpenDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  useEffect(() => {
    if (!selectedTrip) navigate('/');
  }, [selectedTrip, navigate]);

  if (!selectedTrip) return null;

  const handleSeatClick = (seatNum) => {
    if (!activePassengerId) return;

    // If this seat is already assigned to another passenger, swap or deselect
    const currentAssignment = { ...seatAssignments };
    const existingPassenger = Object.entries(currentAssignment).find(
      ([, s]) => s === seatNum
    );

    if (existingPassenger) {
      const [existPId] = existingPassenger;
      if (parseInt(existPId) === activePassengerId) {
        // Deselect
        delete currentAssignment[activePassengerId];
        setSeatAssignments(currentAssignment);
        return;
      }
      // Seat taken by another passenger — do nothing or swap
      return;
    }

    // Assign seat to active passenger
    currentAssignment[activePassengerId] = seatNum;
    setSeatAssignments(currentAssignment);

    // Auto-advance to next passenger without a seat
    const nextPassenger = passengersList.find(
      (p) => p.id !== activePassengerId && !currentAssignment[p.id]
    );
    if (nextPassenger) {
      setActivePassengerId(nextPassenger.id);
    }
  };

  const handleCategoryChange = (passengerId, newCategory) => {
    setPassengersList((prev) =>
      prev.map((p) => (p.id === passengerId ? { ...p, category: newCategory } : p))
    );
    setOpenDropdown(null);
  };

  const handleContinue = () => {
    // All passengers must have seats assigned
    const allAssigned = passengersList.every((p) => seatAssignments[p.id]);
    if (!allAssigned) return;
    setCurrentStep(3);
    navigate('/passenger');
  };

  const basePrice = selectedTrip?.price || 0;
  const busType = (selectedTrip?.type === 'LUX Lounge' || selectedTrip?.busType === 'lounge') ? 'lounge' : 'standard';
  const RELAX_SEATS = busType === 'lounge' ? LOUNGE_RELAX : STANDARD_RELAX;

  const isRelaxSeat = (passengerId) => {
    const seat = seatAssignments[passengerId];
    return seat ? RELAX_SEATS.has(seat) : false;
  };

  // Calculate total
  const totalAmount = passengersList.reduce((sum, p) => {
    const relax = isRelaxSeat(p.id);
    let pTotal = getPassengerPrice(p.category, basePrice, relax);
    if (selectedReturnTrip) {
      pTotal += getPassengerPrice(p.category, selectedReturnTrip.price, relax);
    }
    return sum + pTotal;
  }, 0);

  const allSeatsAssigned = passengersList.every((p) => seatAssignments[p.id]);

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <StepIndicator />

      {/* Route header */}
      <div className="bg-white border-b">
        <div className="max-w-[1280px] mx-auto px-3 md:px-4 py-3 text-center">
          <span className="text-sm font-semibold text-[#c30909]">{selectedTrip.from}</span>
          <span className="text-sm text-[#484c55]/40 mx-2">→</span>
          <span className="text-sm font-semibold text-[#c30909]">{selectedTrip.to}</span>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-3 md:px-4 py-4 md:py-6">
        {/* Info note */}
        <div className="flex items-start gap-2 mb-4 text-xs text-[#555]">
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <p>{t('seat.promoNote')}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Bus seat map */}
          <div className="flex-shrink-0">
            <BusSeatMap
              seatAssignments={seatAssignments}
              occupiedSeats={occupiedSeats}
              activePassengerId={activePassengerId}
              onSeatClick={handleSeatClick}
              busType={selectedTrip?.type === 'LUX Lounge' ? 'lounge' : 'standard'}
            />
          </div>

          {/* Center: Passengers list */}
          <div className="flex-1 space-y-3">
            {passengersList.map((passenger) => {
              const relax = isRelaxSeat(passenger.id);
              const price = getPassengerPrice(passenger.category, basePrice, relax);
              const seat = seatAssignments[passenger.id];
              const isActive = activePassengerId === passenger.id;
              const isDropdownOpen = openDropdown === passenger.id;

              return (
                <div
                  key={passenger.id}
                  className={`bg-white rounded-xl border-2 transition-all ${
                    isActive ? 'border-[#c30909] shadow-md' : 'border-gray-100'
                  }`}
                >
                  {/* Passenger header — click to select */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => setActivePassengerId(passenger.id)}
                  >
                    <div className="flex items-center gap-3">
                      <UserCircle
                        size={24}
                        className={isActive ? 'text-[#c30909]' : 'text-[#484c55]/40'}
                      />
                      <div>
                        <span className="text-sm font-bold">
                          {t('seat.passenger')} {passenger.id}
                        </span>
                        {seat && (
                          <span className="ml-3 text-xs text-[#777] uppercase">
                            {t('seat.seatLabel')} {seat}
                            {relax && <span className="ml-1 text-[#b8960c] font-semibold">Relax</span>}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-bold">{price.toFixed(2)} €</span>
                  </div>

                  {/* Category selector */}
                  <div className="px-4 pb-4">
                    <p className="text-[10px] text-[#777] uppercase tracking-wider mb-1.5">
                      {t('seat.ticketType')}
                    </p>
                    <div
                      className="relative"
                      ref={(el) => (dropdownRefs.current[passenger.id] = el)}
                    >
                      <button
                        className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2.5 text-sm hover:border-gray-300 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(isDropdownOpen ? null : passenger.id);
                        }}
                      >
                        <span className="font-medium">
                          {t(CATEGORY_KEYS[passenger.category].label)}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${
                            passenger.category !== 'adult' || relax ? 'text-[#c30909]' : ''
                          }`}>
                            {price.toFixed(2)} €
                          </span>
                          {isDropdownOpen ? (
                            <ChevronUp size={16} className="text-[#484c55]/40" />
                          ) : (
                            <ChevronDown size={16} className="text-[#484c55]/40" />
                          )}
                        </div>
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white rounded-lg shadow-xl border mt-1 z-50 py-1 max-w-[320px]">
                          {PASSENGER_CATEGORIES.map((cat) => {
                            const catPrice = getPassengerPrice(cat.id, basePrice, relax);
                            const isSelected = passenger.category === cat.id;
                            return (
                              <div
                                key={cat.id}
                                className={`px-4 py-3 cursor-pointer hover:bg-[#f0f0f1] transition-colors ${
                                  isSelected ? 'bg-[#f0f0f1]' : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCategoryChange(passenger.id, cat.id);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-bold">
                                    {t(CATEGORY_KEYS[cat.id].label)}
                                  </span>
                                  <span className={`text-sm font-bold ${
                                    cat.id !== 'adult' ? 'text-[#c30909]' : ''
                                  }`}>
                                    {catPrice.toFixed(2)} €
                                  </span>
                                </div>
                                <p className="text-[10px] text-[#777] mt-0.5">
                                  {t(CATEGORY_KEYS[cat.id].age)}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Promo badge if discounted */}
                    {passenger.category !== 'adult' && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs bg-[#c30909] text-white px-2 py-0.5 rounded font-medium">
                          {t('seat.promoTicket')}
                        </span>
                        <span className="text-[10px] text-[#777]">
                          {t('seat.promoNonRefund')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Hint */}
            {!allSeatsAssigned && (
              <p className="text-xs text-[#777] text-center mt-2">
                {t('seat.clickToSelect')}
              </p>
            )}
          </div>

          {/* Right: Trip details sidebar */}
          <div className="w-full lg:w-[280px] flex-shrink-0">
            <div className="bg-white rounded-xl p-5 shadow-sm sticky top-4">
              <h2 className="text-base font-bold mb-4">{t('seat.tripDetails')}</h2>

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#2e9e5a]" />
                  <span className="text-xs font-semibold text-[#777] uppercase">
                    {t('seat.outboundTrip')}
                  </span>
                  <ChevronDown size={14} className="text-[#484c55]/30 ml-auto" />
                </div>
                <p className="text-sm font-bold">{formatDate(searchParams.departureDate, lang)}</p>
                <p className="text-xs text-[#555] mt-1">
                  {selectedTrip.departureTime} {selectedTrip.from} - {selectedTrip.arrivalTime} {selectedTrip.to}
                </p>
              </div>

              {selectedReturnTrip && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#484c55]/30" />
                    <span className="text-xs font-semibold text-[#777] uppercase">
                      {t('summary.return')}
                    </span>
                  </div>
                  <p className="text-sm font-bold">{formatDate(searchParams.returnDate, lang)}</p>
                  <p className="text-xs text-[#555] mt-1">
                    {selectedReturnTrip.departureTime} {selectedReturnTrip.from} - {selectedReturnTrip.arrivalTime} {selectedReturnTrip.to}
                  </p>
                </div>
              )}

              {/* Per-passenger prices */}
              <div className="border-t pt-3 space-y-1.5">
                {passengersList.map((p) => {
                  const pRelax = isRelaxSeat(p.id);
                  const pPrice = getPassengerPrice(p.category, basePrice, pRelax);
                  return (
                    <div key={p.id} className="flex items-center justify-between text-xs">
                      <span className="text-[#555]">
                        {t('seat.passenger')} {p.id}
                      </span>
                      <span className="font-medium">
                        {pPrice.toFixed(2)} €
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div className="border-t mt-3 pt-3 flex items-center justify-between">
                <span className="text-sm font-bold">{t('seat.totalAmount')}</span>
                <span className="text-lg font-bold">{totalAmount.toFixed(2)} €</span>
              </div>

              {/* Continue button */}
              <button
                onClick={handleContinue}
                disabled={!allSeatsAssigned}
                className={`w-full mt-4 py-3 rounded-lg font-bold text-sm uppercase tracking-wider transition-colors ${
                  allSeatsAssigned
                    ? 'bg-[#c30909] hover:bg-[#a00707] text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('seat.continue')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeatSelectionPage;
